import { countryMap } from 'src/config/Config';

// Datakey Metadata Models
export interface DatakeyMetadata {
    publicFeeType: string;
    marketplace: string;
    datakeyName: string;
    attributes: DatakeyAttributes;
}

export interface DatakeyAttributes {
    type: DatakeyType;
    displayName: string;
    definition: string;
    excludeFromExplanation: boolean;
    valueOptions?: ValueOption[];
}

export interface ValueOption {
    code: string;
    displayValue: string;
}

export type DatakeyType = 'number' | 'boolean' | 'string';

export interface GetDatakeyRequest {
    publicFeeType: string;
    marketplace: string;
    datakeyName: string;
}

export interface GetDatakeyResponse {
    datakey: DatakeyMetadata;
}

export interface CreateDatakeyRequest {
    publicFeeType: string;
    marketplace: string;
    datakeyName: string;
    attributes: DatakeyAttributes;
}

export interface CreateDatakeyResponse {
    datakey: DatakeyMetadata;
}

export interface CreateDatakeyMultiMarketplaceRequest {
    publicFeeType: string;
    marketplaces: string[];
    datakeyName: string;
    attributes: DatakeyAttributes;
}

export interface DatakeyCreationResult {
    marketplace: string;
    status: 'success' | 'skipped' | 'failure';
    datakey?: DatakeyMetadata;
    errorMessage?: string;
}

export interface CreateDatakeyMultiMarketplaceResponse {
    results: DatakeyCreationResult[];
    successCount: number;
    skippedCount: number;
    failureCount: number;
}

export interface UpdateDatakeyRequest {
    publicFeeType: string;
    marketplace: string;
    datakeyName: string;
    attributes: DatakeyAttributes;
}

export interface UpdateDatakeyResponse {
    datakey: DatakeyMetadata;
}

export interface DeleteDatakeyRequest {
    publicFeeType: string;
    marketplace: string;
    datakeyName: string;
}

export interface DeleteDatakeyResponse {
    // Empty response
}

// Creation form state
export interface DatakeyCreationForm {
    datakeyName: string;
    feeType: string;
    marketplace: string;
    selectedMarketplaces: string[];
    type: DatakeyType;
    displayName: string;
    definition: string;
    excludeFromExplanation: boolean;
    valueOptions: ValueOption[];
}

// Pre-fill search state
export interface DatakeyPreFillSearch {
    publicFeeType: string;
    marketplace: string;
    datakeyName: string;
}

// Operation result for success/error feedback
export interface OperationResult {
    type: 'create' | 'update' | 'delete';
    success: boolean;
    message: string;
    datakeyName?: string;
}

// Modal visibility state
export interface ModalVisibilityState {
    showCreateModal: boolean;
    showEditModal: boolean;
    showDeleteModal: boolean;
    showDetailsModal: boolean;
    showResultsModal: boolean;
}

// Redux State Types - Only shareable, persistent data
export interface DatakeyMetadataState {
    searchResults: DatakeyMetadata[];
    searchLoading: boolean;
    searchError: string | null;
    searchNextToken: string | null;
    currentSearchFilters: DatakeySearchFilters;
    operationLoading: boolean;
    multiMarketplaceResults: DatakeyCreationResult[] | null;
    lastOperationResult: OperationResult | null;
    modalVisibility: ModalVisibilityState;
    modalDatakey: DatakeyMetadata | null;
    editingModalDatakey: DatakeyMetadata | null;
    creationForm: DatakeyCreationForm;
    createMode: 'single' | 'multi';
    preFillSearch: DatakeyPreFillSearch;
    preFillLoading: boolean;
    preFillError: string | null;
    showPreFillSection: boolean;
}

// Generic Search Filters (replaces both browse and search)
export interface DatakeySearchFilters {
    feeType?: string;
    datakeyName?: string;
    marketplace?: string;
}

// API Request for search
export interface SearchDatakeysRequest {
    feeType?: string;
    datakeyName?: string;
    marketplace?: string;
    limit?: number;
    nextToken?: string;
}

export interface SearchDatakeysResponse {
    datakeys: DatakeyMetadata[];
    nextToken?: string;
}

// Constants
export const DATAKEY_TYPES: DatakeyType[] = ['number', 'boolean', 'string'];

// Get all marketplaces from countryMap
let MARKETPLACE_OPTIONS = Object.values(countryMap).reduce((acc, countries) => acc.concat(countries), []).sort();
MARKETPLACE_OPTIONS.push('Global');
export { MARKETPLACE_OPTIONS };
